import { inject, injectable } from "@launchtray/tsyringe-async";
import { Bindable } from "bindable-data/dist/binding_data/Bindable";
import { NotifyProperty } from "bindable-data/dist/binding_data/NotifyProperty";
import { nameof } from "bindable-data";
import { BundleResourcesPackage, IPackageRegistrator } from "resource-system";
import { InjectableInterfaces } from "../../../composition/InjectableInterfaces";
import { MenuResourcesPackage } from "../../menu/resources/MenuResourcesPackage";
import { LocalizationResourcesPackage } from "../../../localization/LocalizationResourcesPackage";
import { SettingsResourcesPackage } from "../../popups/settings/resources/SettingsResourcesPackage";
import { CommonResourcesPackage } from "../../../common/resources/CommonResourcesPackage";
import { VictoryResourcesPackage } from "../../popups/victory/resources/VictoryResourcesPackage";
import { LoseResourcesPackage } from "../../popups/lose/resources/LoseResourcesPackage";
import { PauseResourcesPackage } from "../../popups/pause/resources/PauseResourcesPackage";
import { LevelsResourcesPackage } from "../../../localization/LevelsResourcesPackage";
import { GameResourcesPackage } from "../../game/resources/GameResourcesPackage";
import { SoundResourcesPackage } from "../../../common/resources/SoundResourcesPackage";
import { SpineResourcesPackage } from "../../../common/resources/SpineResourcesPackage";
import { IGameDistributionSdk } from "../../../gamedistribution/interfaces/IGameDistributionSdk";

@injectable()
export class PreloaderModel extends Bindable {
    public readonly loadingProgress: NotifyProperty<number>;
    private readonly packagesToPreload: Map<new () => BundleResourcesPackage, BundleResourcesPackage>;
    private readonly packagesAmount: number;
    private progress: number;

    public constructor(
        @inject(InjectableInterfaces.IPackageRegistrator)
        private readonly packageRegistrator: IPackageRegistrator,
        @inject(InjectableInterfaces.IGameDistributionSdk) private readonly gdSdk: IGameDistributionSdk
    ) {
        super();
        this.loadingProgress = new NotifyProperty(this, nameof(this, "loadingProgress"), 0);
        this.packagesToPreload = new Map<new () => BundleResourcesPackage, BundleResourcesPackage>([
            [LocalizationResourcesPackage, new LocalizationResourcesPackage()],
            [LevelsResourcesPackage, new LevelsResourcesPackage()],
            [SoundResourcesPackage, new SoundResourcesPackage()],
            [CommonResourcesPackage, new CommonResourcesPackage()],
            [MenuResourcesPackage, new MenuResourcesPackage()],
            [SettingsResourcesPackage, new SettingsResourcesPackage()],
            [GameResourcesPackage, new GameResourcesPackage()],
            [PauseResourcesPackage, new PauseResourcesPackage()],
            [VictoryResourcesPackage, new VictoryResourcesPackage()],
            [LoseResourcesPackage, new LoseResourcesPackage()],
            [SpineResourcesPackage, new SpineResourcesPackage()]
        ]);
        this.packagesAmount = this.packagesToPreload.size /*+ GD SDK*/ + 1;
        this.progress = 0;
    }

    public async startPreloadingPackages(): Promise<void> {
        const loadingTasks = new Array<Promise<BundleResourcesPackage>>();
        for (const pkg of this.packagesToPreload.values()) {
            let previousProgress = 0;
            pkg.onLoadingProgress.add((packageProgress: number) => {
                this.progress -= previousProgress / this.packagesAmount;
                this.progress += packageProgress / this.packagesAmount;
                previousProgress = packageProgress;
                this.loadingProgress.setValue(this.progress);
            }, this);
            try {
                loadingTasks.push(pkg.load());
            } catch (e) {
                console.error(e);
            }
        }
        await Promise.all([...loadingTasks, this.gdSdk.initialize()]);
        for (const pkgType of this.packagesToPreload.keys()) {
            const pkg = this.packagesToPreload.get(pkgType);
            this.packageRegistrator.registerPackage(pkgType, pkg);
            pkg.onLoadingProgress.removeAll();
        }
        this.loadingProgress.setValue(1);
    }
}